import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { Box, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import classNames from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalName } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { modalsReducers } from 'app/store/modal/slice';
import { dispatchUnwrap } from 'app/utils/store';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { BreakTimer } from 'modules/bracket/components/BreakTimer';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import { markAsReady } from 'modules/tournaments/store/current-tournament/repository';
import { tournamentActivitySelector } from 'modules/tournament-activity/store/selectors';
import { sessionSelector } from 'app/store/session/selectors';
import { BracketTournamentCodeModalPayload } from 'modules/bracket/components/BracketTournamentCodeModal';
import { useTournamentMatchTitle } from 'modules/tournaments/hooks';
import { useStyles } from '../../styles/TournamentGameActivity.styles';

interface NewTournamentGameProps {
  onTournamentClick: () => void;
}

export const NewTournamentGame: FC<NewTournamentGameProps> = ({ onTournamentClick }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { getTournamentMatchGameTitle } = useTournamentMatchTitle();

  const isLessThanXxs = useMediaQuery(theme.breakpoints.down('x-xs'));
  const tournamentActivity = useSelector(tournamentActivitySelector.activity);

  const name = useSelector(tournamentActivitySelector.name);
  const currentUser = useSelector(sessionSelector.activeUser);
  const timerDuration = useSelector(tournamentActivitySelector.timerDuration);
  const timerStartedAt = useSelector(tournamentActivitySelector.timerStartedAt);
  const tournamentId = useMemo(
    () => tournamentActivity.tournament?.id ?? null,
    [tournamentActivity.tournament?.id]
  );

  const isLoading = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Joining,
  });

  const description = useMemo(() => {
    if (!tournamentActivity?.tournament || !tournamentActivity?.matchGame) {
      return '';
    }

    const gameNumbers = {
      roundNumber: tournamentActivity.matchGame.round ?? 0,
      matchNumber: tournamentActivity.matchGame.match ?? 0,
      groupNumber: tournamentActivity.matchGame.group ?? 0,
      matchGameNumber: tournamentActivity.matchGame.game ?? 0,
    };
    const title = getTournamentMatchGameTitle(gameNumbers, tournamentActivity.tournament);
    return `${title} is started!`;
  }, [getTournamentMatchGameTitle, tournamentActivity.tournament, tournamentActivity.matchGame]);

  const isCurrentUserJoined = useMemo(() => {
    if (!tournamentActivity?.matchGame?.readyUsers?.length || !currentUser?.id) {
      return false;
    }

    const users = tournamentActivity.matchGame.readyUsers.map((data) => data.user);
    return users.includes(currentUser.id);
  }, [currentUser?.id, tournamentActivity]);

  const onClickHandler = useCallback(() => {
    if (!tournamentId || !tournamentActivity.matchGame?.id) {
      return;
    }

    const { matchGame } = tournamentActivity;

    const data: BracketTournamentCodeModalPayload = {
      tournamentId,
      tournamentMatchGameId: matchGame.gameIndex.toString(),
    };

    if (isCurrentUserJoined) {
      dispatch(modalsReducers.openModal({ name: ModalName.TournamentCode, data }));
      return;
    }

    dispatchUnwrap(markAsReady({ tournamentId: tournamentId, payload: { gameId: matchGame.id } }), {
      success: () => {
        if (!matchGame?.game || !tournamentId) {
          return;
        }

        dispatch(modalsReducers.openModal({ name: ModalName.TournamentCode, data }));
      },
    });
  }, [tournamentActivity, tournamentId, isCurrentUserJoined, dispatch]);

  if (!tournamentActivity) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.container}>
        <Typography className={classes.lineClampText}>
          <Link onClick={onTournamentClick}>{name}</Link>
          <Typography component="span" pr={0.5} pl={0.5}>
            -
          </Typography>
          <Typography component="span">{description}</Typography>
        </Typography>

        <LoaderButton
          color="primary"
          variant="contained"
          loading={isLoading}
          onClick={onClickHandler}
          className={classNames(classes.button)}
        >
          <Typography className={classes.buttonText}>
            {!isLessThanXxs ? (
              <Typography component="span">Play Now</Typography>
            ) : (
              <SportsEsportsIcon />
            )}
            {timerStartedAt && (
              <Typography component="span" pl={0.5}>
                <BreakTimer prefix="" startedAt={timerStartedAt} duration={timerDuration} />
              </Typography>
            )}
          </Typography>
        </LoaderButton>
      </Box>
    </Box>
  );
};
